import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CheckCircle2,
  History,
  Loader2,
  RefreshCw,
  UserRoundCog,
  XCircle
} from 'lucide-react';
import { adminCapabilityAPI, stewardChangeAPI } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { hasAdminPermission } from '../utils/adminPermissions';
import {
  ADMIN_FEATURE_CODES,
  isAdminFeatureUnavailableError,
  readAdminFeatureAvailability
} from '../utils/adminFeatureAvailability';
import AdminModal from './AdminModal';

const copy = {
  zh: {
    title: '水管家换人审核',
    subtitle: '审核 KUD 换人申请并查看不可变更的指派历史。',
    all: '全部',
    pending: '待审核',
    approved: '已批准',
    rejected: '已拒绝',
    refresh: '刷新',
    retry: '重试',
    empty: '没有符合条件的申请',
    unavailable: '此生产环境尚未启用水管家换人流程。现有指派与历史资料不会被修改。',
    unit: '设备',
    from: '原水管家',
    to: '新水管家',
    reason: '申请原因',
    approve: '批准',
    reject: '拒绝',
    direct: '总部直接换人',
    decisionReason: '审核／直接换人原因',
    stewardId: '新水管家 ID',
    expectedVersion: '设备版本',
    confirmDirect: '确认总部直接换人',
    confirmDirectHint: '这会立即生效、写入历史，并使当前 Pending 申请失效。',
    history: '指派历史',
    cancel: '取消',
    save: '确认执行'
  },
  id: {
    title: 'Tinjauan pergantian steward',
    subtitle: 'Tinjau permintaan KUD dan riwayat penugasan yang tidak dapat diubah.',
    all: 'Semua',
    pending: 'Menunggu',
    approved: 'Disetujui',
    rejected: 'Ditolak',
    refresh: 'Segarkan',
    retry: 'Coba lagi',
    empty: 'Tidak ada permintaan',
    unavailable: 'Alur pergantian steward belum diaktifkan di lingkungan produksi ini. Penugasan dan riwayat yang ada tidak akan diubah.',
    unit: 'Perangkat',
    from: 'Steward lama',
    to: 'Steward baru',
    reason: 'Alasan',
    approve: 'Setujui',
    reject: 'Tolak',
    direct: 'Ganti langsung oleh HQ',
    decisionReason: 'Alasan keputusan',
    stewardId: 'ID steward baru',
    expectedVersion: 'Versi perangkat',
    confirmDirect: 'Konfirmasi pergantian langsung',
    confirmDirectHint: 'Perubahan langsung berlaku, dicatat, dan membatalkan permintaan Pending.',
    history: 'Riwayat',
    cancel: 'Batal',
    save: 'Konfirmasi'
  },
  en: {
    title: 'Steward change review',
    subtitle: 'Review KUD requests and immutable assignment history.',
    all: 'All',
    pending: 'Pending',
    approved: 'Approved',
    rejected: 'Rejected',
    refresh: 'Refresh',
    retry: 'Retry',
    empty: 'No matching requests',
    unavailable: 'The steward-change workflow is not enabled in this production environment. Existing assignments and history will not be changed.',
    unit: 'Unit',
    from: 'Previous steward',
    to: 'New steward',
    reason: 'Request reason',
    approve: 'Approve',
    reject: 'Reject',
    direct: 'HQ direct change',
    decisionReason: 'Decision/direct-change reason',
    stewardId: 'New steward ID',
    expectedVersion: 'Unit version',
    confirmDirect: 'Confirm HQ direct change',
    confirmDirectHint: 'This takes effect immediately, writes history, and supersedes any Pending request.',
    history: 'Assignment history',
    cancel: 'Cancel',
    save: 'Confirm action'
  }
};

const languageKey = () => {
  const language = navigator.language || 'en';
  return language.startsWith('zh') ? 'zh' : language.startsWith('id') ? 'id' : 'en';
};

export default function StewardChangeManagement() {
  const user = useAuthStore((state) => state.user);
  const language = languageKey();
  const text = copy[language];
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState('Pending');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [availability, setAvailability] = useState('checking');
  const [writeEnabled, setWriteEnabled] = useState(false);
  const [capabilityAttempt, setCapabilityAttempt] = useState(0);
  const [modal, setModal] = useState(null);
  const [reason, setReason] = useState('');
  const [stewardId, setStewardId] = useState('');
  const [version, setVersion] = useState('');
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  const allowed = hasAdminPermission(user, 'review_steward_changes');

  const load = useCallback(async () => {
    if (!allowed || availability !== 'enabled') return;
    setLoading(true);
    setError('');
    try {
      const response = await stewardChangeAPI.list(
        status === 'All' ? {} : { status }
      );
      setRows(response.data?.data || []);
    } catch (value) {
      const unavailable = isAdminFeatureUnavailableError(
        value,
        ADMIN_FEATURE_CODES.stewardChangeWorkflow
      );
      if (unavailable) {
        setAvailability('unavailable');
        setWriteEnabled(false);
        setRows([]);
        setModal(null);
      }
      setError(unavailable ? '' : (value.response?.data?.message || value.message));
    } finally {
      setLoading(false);
    }
  }, [allowed, availability, status]);

  useEffect(() => {
    if (!allowed) {
      setLoading(false);
      return undefined;
    }

    let active = true;
    const checkAvailability = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await adminCapabilityAPI.get();
        const feature = readAdminFeatureAvailability(
          response,
          'stewardChangeWorkflow'
        );
        if (!active) return;
        setWriteEnabled(feature.writeEnabled === true);
        setAvailability(feature.enabled ? 'enabled' : 'unavailable');
      } catch (value) {
        if (!active) return;
        if (value?.response?.status === 404) {
          setAvailability('enabled');
          setWriteEnabled(false);
        } else {
          setAvailability('error');
          setError(value.response?.data?.message || value.message);
          setLoading(false);
        }
      }
    };
    checkAvailability();
    return () => {
      active = false;
    };
  }, [allowed, capabilityAttempt]);

  useEffect(() => {
    if (availability !== 'enabled') {
      if (availability === 'unavailable') setLoading(false);
      return;
    }
    load();
  }, [availability, load]);

  const counts = useMemo(
    () => rows.reduce(
      (result, row) => ({
        ...result,
        [row.status]: (result[row.status] || 0) + 1
      }),
      {}
    ),
    [rows]
  );

  const openHistory = async (row) => {
    setModal({ type: 'history', row });
    setError('');
    try {
      const response = await stewardChangeAPI.history(row.unitId);
      setHistory(response.data?.data || []);
    } catch (value) {
      setError(value.response?.data?.message || value.message);
    }
  };

  const submitReview = async () => {
    setSaving(true);
    try {
      await stewardChangeAPI.review(modal.row.id, {
        decision: modal.type,
        reason
      });
      setModal(null);
      setReason('');
      await load();
    } catch (value) {
      setError(value.response?.data?.message || value.message);
    } finally {
      setSaving(false);
    }
  };

  const submitDirect = async () => {
    setSaving(true);
    try {
      await stewardChangeAPI.directChange(Number(modal.unitId), {
        stewardId: Number(stewardId),
        expectedVersion: Number(version),
        reason
      });
      setModal(null);
      setReason('');
      setStewardId('');
      setVersion('');
      await load();
    } catch (value) {
      setError(value.response?.data?.message || value.message);
    } finally {
      setSaving(false);
    }
  };

  if (!allowed) return <div className="p-8 text-rose-400">403</div>;

  return (
    <div className="min-h-screen p-4 md:p-8">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="admin-page-title">{text.title}</h1>
          <p className="admin-text-secondary mt-2">{text.subtitle}</p>
        </div>
        {availability === 'enabled' && (
          <div className="flex gap-2">
            {writeEnabled && (
              <button
                type="button"
                onClick={() => setModal({ type: 'direct', unitId: '' })}
                className="admin-button-secondary rounded-xl px-4 py-2"
              >
                <UserRoundCog size={17} className="mr-2 inline" />
                {text.direct}
              </button>
            )}
            <button
              type="button"
              onClick={load}
              className="admin-button-secondary rounded-xl px-4 py-2"
            >
              <RefreshCw size={17} className="mr-2 inline" />
              {text.refresh}
            </button>
          </div>
        )}
      </div>

      {availability === 'unavailable' && (
        <div className="admin-card rounded-2xl p-10 text-center admin-text-secondary">
          {text.empty}
        </div>
      )}

      {error && availability !== 'unavailable' && (
        <div className="mb-4 rounded-xl border border-rose-400/30 bg-rose-500/10 p-3 text-rose-500">
          <p>{error}</p>
          <button
            type="button"
            onClick={
              availability === 'error'
                ? () => setCapabilityAttempt((value) => value + 1)
                : load
            }
            className="admin-button-secondary mt-3 rounded-lg px-3 py-2"
          >
            {text.retry}
          </button>
        </div>
      )}

      {availability === 'enabled' && (
        <>
          <div className="mb-5 flex flex-wrap gap-2">
            {['All', 'Pending', 'Approved', 'Rejected'].map((item) => (
              <button
                type="button"
                key={item}
                onClick={() => setStatus(item)}
                className={`rounded-xl px-4 py-2 text-sm font-bold ${
                  status === item
                    ? 'bg-cyan-500 text-slate-950'
                    : 'admin-button-secondary'
                }`}
              >
                {text[item.toLowerCase()] || item}
                {counts[item] ? ` (${counts[item]})` : ''}
              </button>
            ))}
          </div>

          {loading ? (
            <Loader2 className="animate-spin text-cyan-400" />
          ) : rows.length === 0 ? (
            <div className="admin-card rounded-2xl p-10 text-center admin-text-secondary">
              {text.empty}
            </div>
          ) : (
            <div className="grid gap-4">
              {rows.map((row) => (
                <article key={row.id} className="admin-card rounded-2xl p-5">
                  <div className="flex flex-wrap justify-between gap-3">
                    <div>
                      <h2 className="admin-text-primary text-lg font-black">
                        {text.unit} #{row.unitId}
                      </h2>
                      <p className="admin-text-secondary mt-1 text-sm">
                        {text.from} #{row.fromStewardId} → {text.to} #{row.toStewardId}
                        {' '}· v{row.expectedUnitVersion}
                      </p>
                      <p className="admin-text-secondary mt-2">
                        {text.reason}: {row.reason}
                      </p>
                      {row.decisionReason && (
                        <p className="mt-2 text-amber-400">{row.decisionReason}</p>
                      )}
                    </div>
                    <span className="h-fit rounded-full bg-slate-500/15 px-3 py-1 text-sm font-bold">
                      {row.status}
                    </span>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => openHistory(row)}
                      className="admin-button-secondary rounded-lg px-3 py-2"
                    >
                      <History size={16} className="mr-2 inline" />
                      {text.history}
                    </button>
                    {writeEnabled && row.status === 'Pending' && (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            setReason('');
                            setModal({ type: 'approve', row });
                          }}
                          className="rounded-lg bg-emerald-500 px-3 py-2 font-bold text-slate-950"
                        >
                          <CheckCircle2 size={16} className="mr-2 inline" />
                          {text.approve}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setReason('');
                            setModal({ type: 'reject', row });
                          }}
                          className="rounded-lg bg-rose-500 px-3 py-2 font-bold text-white"
                        >
                          <XCircle size={16} className="mr-2 inline" />
                          {text.reject}
                        </button>
                      </>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}
        </>
      )}

      <AdminModal
        open={Boolean(modal)}
        onClose={() => setModal(null)}
        title={
          modal?.type === 'history'
            ? text.history
            : modal?.type === 'direct'
              ? text.confirmDirect
              : modal?.type === 'approve'
                ? text.approve
                : text.reject
        }
        description={modal?.type === 'direct' ? text.confirmDirectHint : undefined}
        footer={modal?.type !== 'history' && (
          <>
            <button
              type="button"
              onClick={() => setModal(null)}
              className="admin-button-secondary rounded-xl px-4 py-2"
            >
              {text.cancel}
            </button>
            <button
              type="button"
              disabled={saving || !reason.trim()}
              onClick={modal?.type === 'direct' ? submitDirect : submitReview}
              className="rounded-xl bg-cyan-500 px-5 py-2 font-black text-slate-950 disabled:opacity-50"
            >
              {text.save}
            </button>
          </>
        )}
      >
        {modal?.type === 'history' ? (
          <div className="space-y-3">
            {history.map((item) => (
              <div key={item.id} className="rounded-xl bg-slate-500/10 p-3">
                <strong>{item.action} · v{item.assignmentVersion}</strong>
                <p className="admin-text-secondary text-sm">
                  #{item.fromStewardId || '-'} → #{item.toStewardId || '-'} · {item.reason}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {modal?.type === 'direct' && (
              <>
                <input
                  value={modal.unitId}
                  onChange={(event) => setModal({ ...modal, unitId: event.target.value })}
                  className="admin-input w-full rounded-xl p-3"
                  placeholder={text.unit}
                />
                <input
                  value={stewardId}
                  onChange={(event) => setStewardId(event.target.value)}
                  className="admin-input w-full rounded-xl p-3"
                  placeholder={text.stewardId}
                />
                <input
                  value={version}
                  onChange={(event) => setVersion(event.target.value)}
                  className="admin-input w-full rounded-xl p-3"
                  placeholder={text.expectedVersion}
                />
              </>
            )}
            <textarea
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              className="admin-input min-h-28 w-full rounded-xl p-3"
              placeholder={text.decisionReason}
            />
          </div>
        )}
      </AdminModal>
    </div>
  );
}
